"use client"
import React, { useState } from 'react';
import { ToolExecution } from '@/services/models/streaming-reponse';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { motion, AnimatePresence } from "motion/react";

interface EONThinkingProps {
    toolExecutions: ToolExecution[];
    compactMobileSpacing?: boolean;
}

interface ToolExecutionCardProps {
    execution: ToolExecution;
    index: number;
}

const ToolExecutionCard: React.FC<ToolExecutionCardProps> = ({ execution, index }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isCalling = execution.status === 'calling';
    // Auto-expand when calling to show payload, but allow manual toggle
    const shouldShowContent = isExpanded || isCalling;

    const getStatusPill = (status: ToolExecution['status']) => {
        const baseClasses = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium";
        let colors = { bg: "#f3f4f6", text: "#1f2937" }; // default gray
        let label: string = status;

        switch (status) {
            case 'calling':
                colors = { bg: "#fef3c7", text: "#92400e" }; // amber-100, amber-800
                label = "Executing...";
                break;
            case 'completed':
                colors = { bg: "#dcfce7", text: "#166534" }; // green-100, green-800
                label = "Completed";
                break;
            case 'error':
                colors = { bg: "#fee2e2", text: "#991b1b" }; // red-100, red-800
                label = "Error";
                break;
        }

        return (
            <motion.span
                className={baseClasses}
                animate={{ 
                    backgroundColor: colors.bg, 
                    color: colors.text 
                }}
                transition={{ duration: 0.4 }}
            >
                {label}
            </motion.span>
        );
    };

    const formatDuration = (ms: number | null) => {
        if (!ms) return null;
        if (ms < 1000) return `${ms.toFixed(0)}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const formatToolName = (toolName: string) => {
        return toolName
            .replace(/[_-]/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    const formatResult = (result: any) => {
        if (typeof result !== 'string') return result;
        return result
            .replace(/\\n/g, '\n')
            .replace(/\\u0022/g, '"');
    };

    // Check if there's any content to show (arguments, result, error, pluginName)
    const hasContentToShow = execution.pluginName || 
        (execution.arguments && Object.keys(execution.arguments).length > 0) ||
        execution.result ||
        execution.error;

    return (
        <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="border border-gray-200 rounded-lg bg-white overflow-hidden"
        >
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-2 text-left hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-800 truncate">
                        {formatToolName(execution.toolName)}
                    </span>
                    {getStatusPill(execution.status)}
                    {execution.durationMs && (
                        <span className="text-xs text-gray-500">
                            • {formatDuration(execution.durationMs)}
                        </span>
                    )}
                </div>
                {hasContentToShow && (
                    isExpanded ? (
                        <MdExpandLess className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                    ) : (
                        <MdExpandMore className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                    )
                )}
            </button>

            {shouldShowContent && hasContentToShow && (
                <div className="px-2 pb-2 pt-1 border-t border-gray-100 space-y-2">
                    {execution.pluginName && (
                        <div className="text-xs text-gray-600">
                            <span className="font-medium">Plugin:</span> {execution.pluginName}
                        </div>
                    )}
                    
                    {execution.arguments && Object.keys(execution.arguments).length > 0 && (
                        <div className="text-xs">
                            <div className="text-gray-600 hover:text-gray-800 font-medium mb-1">
                                Arguments
                            </div>
                            <pre className="mt-1 p-2 bg-gray-50 rounded border text-xs max-h-48 overflow-y-auto overflow-x-auto">
                                {JSON.stringify(execution.arguments, null, 2)}
                            </pre>
                        </div>
                    )}

                    {execution.result && (
                        <details className="text-xs">
                            <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                Result
                            </summary>
                            <pre className="mt-1 p-2 bg-gray-50 rounded border text-xs max-h-48 overflow-y-auto overflow-x-auto whitespace-pre-wrap break-words">
                                {formatResult(execution.result)}
                            </pre>
                        </details>
                    )}

                    {execution.error && (
                        <div className="text-xs text-red-700 bg-red-50 p-2 rounded border border-red-200 max-h-48 overflow-y-auto overflow-x-auto whitespace-pre-wrap break-words">
                            <span className="font-medium">Error:</span> {execution.error}
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
};

export const EONThinking: React.FC<EONThinkingProps> = ({ toolExecutions, compactMobileSpacing = false }) => {
    // Don't render if there are no tool executions
    if (!toolExecutions || toolExecutions.length === 0) {
        return null;
    }

    return (
        <div className={`mb-3 space-y-2 ${compactMobileSpacing ? 'space-y-1.5' : ''}`}>
            {toolExecutions.map((execution, index) => {
                return (
                    <ToolExecutionCard 
                        key={`${execution.toolCallId || execution.toolName}-${index}`}
                        execution={execution}
                        index={index}
                    />
                );
            })}
        </div>
    );
};
